import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Newspaper, Heart } from "lucide-react";
import { useSystemSettings } from "../../lib/systemSettings";

/**
 * News-/Werbebeiträge im Kundenportal.
 *
 * Phase-A-Änderungen:
 *  - Bild-Layout auf Werbeanzeigen-Format optimiert. Motive im Hoch-
 *    format bleiben ohne Cropping sichtbar (``object-contain`` auf
 *    dunklem Backdrop), Motive im Querformat füllen die volle Breite.
 *  - Herz-Reactions (Toggle-Like) mit persistentem Zähler aus dem
 *    Backend (``POST /api/portal/news/{id}/like``). Optimistische
 *    UI-Aktualisierung + Rollback bei Serverfehler.
 */
export default function PortalNews() {
  const qc = useQueryClient();
  const { brandName } = useSystemSettings();
  const q = useQuery({
    queryKey: ["portal-news"],
    queryFn: async () => (await api.get("/portal/news")).data,
  });
  const posts = q.data || [];

  const likeMut = useMutation({
    mutationFn: async (pid) => (await api.post(`/portal/news/${pid}/like`)).data,
    // Optimistic update — sofortiges Feedback für den Nutzer.
    onMutate: async (pid) => {
      await qc.cancelQueries({ queryKey: ["portal-news"] });
      const prev = qc.getQueryData(["portal-news"]);
      qc.setQueryData(["portal-news"], (old) =>
        (old || []).map((p) =>
          p.id === pid
            ? { ...p, liked: !p.liked, like_count: Math.max(0, (p.like_count || 0) + (p.liked ? -1 : 1)) }
            : p
        )
      );
      return { prev };
    },
    onError: (e, _pid, ctx) => {
      // Rollback + freundliche Meldung.
      if (ctx?.prev) qc.setQueryData(["portal-news"], ctx.prev);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reaction fehlgeschlagen");
    },
    onSuccess: (data, pid) => {
      // Server-Werte gewinnen — vermeidet Drift bei parallelen Klicks.
      qc.setQueryData(["portal-news"], (old) =>
        (old || []).map((p) => (p.id === pid ? { ...p, liked: data.liked, like_count: data.like_count } : p))
      );
    },
  });

  return (
    <div className="space-y-4" data-testid="portal-news">
      <div>
        <div className="text-3xl font-black uppercase tracking-tight">News & Aktuelle Angebote</div>
        <div className="text-sm text-zinc-500">
          {brandName ? `Aktuelles rund um ${brandName}.` : "Aktuelles."}
        </div>
      </div>
      {q.isLoading && <div className="text-zinc-500">Lade…</div>}
      {!q.isLoading && posts.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-zinc-500">
          Aktuell keine Beiträge.
        </div>
      )}
      <div className="grid gap-6">
        {posts.map((p) => (
          <article
            key={p.id}
            data-testid={`portal-post-${p.id}`}
            className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900 shadow-lg"
          >
            {p.image_url && (
              // Werbemotive laufen häufig im Hochformat (4:5 / 9:16). Damit
              // wichtige Bildinhalte nicht angeschnitten werden, zeigen wir
              // das Motiv mit ``object-contain`` auf einem dunklen Backdrop.
              // Die maximale Höhe orientiert sich am Viewport, damit auf dem
              // Handy nicht die halbe Seite von einem Bild belegt wird.
              <div className="w-full bg-zinc-950 flex items-center justify-center">
                <img
                  src={p.image_url}
                  alt=""
                  className="max-h-[70vh] sm:max-h-[560px] w-auto max-w-full object-contain"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-2">
                <Newspaper size={14} />
                <span>{new Date(p.published_at || p.created_at).toLocaleDateString("de-DE")}</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">{p.title}</h2>
              <div className="mt-3 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{p.body}</div>
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-3">
                <button
                  type="button"
                  data-testid={`portal-post-${p.id}-like`}
                  onClick={() => likeMut.mutate(p.id)}
                  disabled={likeMut.isPending}
                  aria-pressed={!!p.liked}
                  aria-label={p.liked ? "Like entfernen" : "Beitrag liken"}
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border transition-colors ${
                    p.liked
                      ? "border-rose-500/60 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20"
                      : "border-zinc-800 text-zinc-300 hover:border-rose-500/40 hover:text-rose-400"
                  }`}
                >
                  <Heart
                    size={16}
                    className="shrink-0"
                    fill={p.liked ? "currentColor" : "none"}
                  />
                  <span
                    data-testid={`portal-post-${p.id}-like-count`}
                    className="text-sm tabular-nums"
                  >
                    {Number(p.like_count || 0).toLocaleString("de-DE")}
                  </span>
                </button>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                  {p.liked ? "Gefällt dir" : "Gefällt mir"}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
